/* eslint-disable prefer-destructuring, jsx-a11y/media-has-caption */
import React, { forwardRef, useEffect, useRef, useReducer, useState } from 'react';
import PropTypes from 'prop-types';
import videojs from 'video.js';
import styled from '@emotion/styled';
import classNames from 'classnames';
import VideoCtaBanner from '../VideoCtaBanner';
import VideoEndScreen from './VideoEndScreen';
import VideoPlaybackRateButtons from './VideoPlaybackRateButtons';
import { handleCueChange } from './VideoPlayerHelpers/CaptionsHelpers';
import {
  addPlayerMessageEventHandler,
  removePlayerMessageEventHandler,
} from './videoPlayerMessageEvents';
import { videojsDefaultSkin, colors, transitionTransformMedium, absoluteTopLeft } from '@/styles';
import { MetroHelper, MetroUser, MetroEvent } from '@/classes';
import { ClickableElement } from '@/atoms';
import { getDeviceMediaInfo, registerVjsComponent, useEnsuredForwardedRef } from '@/functions';
import '@streamroot/videojs-hlsjs-plugin';
import { LargePlay } from '@/svgs';

/**
 * VideoPlayer component
 * Custom Inner Components should be registered with the base VideoJS Component class
 * Default VideoJS children can be enabled/disabled in the 'BASE_VIDEOJS_OPTIONS'
 *
 * VideoPlayer Children API here: https://docs.videojs.com/tutorial-options.html#children-1
 * VideoJS Components API here: https://docs.videojs.com/tutorial-components.html
 *  -> also there is a helpful JSBin example under the 'Creating a Component' header
 * VideoJS and React Example here: https://docs.videojs.com/tutorial-react.html
 *
 * @version 1.0.0
 */

const hlsjsConfig = {
  debug: false,
  /**
   * webworkify-webpack is wonky with '@streamroot/videojs-hlsjs-plugin'  https://github.com/video-dev/hls.js/issues/2064#issuecomment-469888615
   * Workarounds:
   *  change hlsjs config debug to true (this creates an esm cueHandler),
   *  change '@streamroot/videojs-hlsjs-plugin' project's webpack targetLibrary
   *     config to `umd` from `window`,
   *  disabling webworker for hls.js.
   */
  enableWorker: false,
};

/** @type {videojs.PlayerOptions} */
const BASE_VIDEOJS_OPTIONS = {
  posterImage: true,
  controls: true,
  fluid: true,
  controlBar: { pictureInPictureToggle: false },
  textTrackDisplay: true,
  textTrackSettings: false,
  loadingSpinner: false,
  errorDisplay: false,
  bigPlayButton: false,
  playbackRates: [1, 1.5, 2],
  html5: {
    hlsjsConfig,
    nativeTextTracks: false,
  },
  inactivityTimeout: 1000,
};

const CTA_BANNER_NAME = 'cta-banner';
const END_SCREEN_NAME = 'end-screen';
const PLAYBACK_RATE_BUTTONS = 'playback-rate-buttons';
const SMALL_VIDEO_SIZE = 375;

function getRootNode(ref) {
  /* eslint-disable-next-line no-restricted-globals */
  return ref?.current?.getRootNode() ?? document;
}

/** @typedef {import('./VideoPlayer').PlayButtonState} PlayButtonState */

/**
 * Reducer for handling play button state changes.
 *
 * @param {PlayButtonState} state
 * @param {import('./VideoPlayer').PlayButtonAction} action
 * @return {PlayButtonState}
 */
export function playButtonReducer(state, action) {
  /**
   * @param {PlayButtonState} newState
   * @returns {PlayButtonState}
   */
  function withNoPlayButton(newState) {
    return { ...newState, showPlayButton: false };
  }

  switch (action.type) {
    case 'ended':
      return withNoPlayButton(state);
    case 'pause':
      // if we are not in playPauseMode, we will never show the play button
      if (state.playPauseMode === false || state.isDesktop) {
        return withNoPlayButton(state);
      }

      // some browsers/devices will pause the video before playing, this will
      // make sure we don't change the showPlayButton state for those cases
      if (!state.hasPlayed) {
        return state;
      }

      // as long as the user is not seeking with the scrub bar, we can
      // show the play button while paused
      return { ...state, showPlayButton: !state.isSeeking };
    case 'play':
      return withNoPlayButton({ ...state, hasPlayed: true });
    case 'seeking':
      return withNoPlayButton({ ...state, seeking: true });
    case 'seeked':
      return { ...state, seeking: false };
    default:
    /* intentional to show this can passthrough on unknown actions */
  }
  return state;
}

/**
 * Figure out if we can show the play button initially
 *
 * @param {boolean} isDesktop
 * @param {string} thumbUrl
 * @param {boolean} enabled
 * @returns {boolean}
 */
function canShowPlayButton(isDesktop, thumbUrl, enabled) {
  // never show big play button on desktop
  if (isDesktop) {
    return false;
  }

  // do not show the play button if we have a thumbnail (video is
  // in preview/embed mode)
  if (thumbUrl !== '') {
    return false;
  }

  return enabled;
}

/**
 * Initialize the state from the initial play mode
 * @param {boolean} enabled
 * @param {string} thumbUrl
 * @return {PlayButtonState}
 */
export function initialPlayButtonState(enabled, thumbUrl) {
  /** @type {import('@bblabs/functions/src/getDeviceMediaInfo').DeviceMediaInfo} */
  const deviceInfo = getDeviceMediaInfo();
  return {
    isDesktop: deviceInfo.isDesktop,
    hasPlayed: false,
    seeking: false,
    showPlayButton: canShowPlayButton(deviceInfo.isDesktop, thumbUrl, enabled),
    playPauseMode: enabled,
  };
}

/**
 * @param {import('./VideoPlayer').VideoPlayerProps} props
 * @param {React.ForwardedRef} ref
 */
function VideoPlayer(
  {
    autoplay,
    className,
    clientId,
    ctaBannerProps,
    enableAudioTrackButton,
    enableFullscreenControl,
    endScreenProps,
    environment,
    eventListeners,
    playerListener,
    playPauseMode,
    shouldPlayInline,
    sources,
    subscriptionId,
    subscriptionStatus,
    subtitleFileSrc,
    thumbUrl,
    videoPlayMetadata,
    isRebrand,
  },
  ref,
) {
  const videoPlayerRef = useEnsuredForwardedRef(ref);
  /** @type {React.MutableRefObject<HTMLVideoElement>} */
  const videoRef = useRef(null);
  /** @type {[PlayButtonState, React.Dispatch<PlayButtonAction>]} */
  const [playButtonState, dispatch] = useReducer(
    playButtonReducer,
    initialPlayButtonState(playPauseMode, thumbUrl),
  );
  const [videoWidth, setVideoWidth] = useState();

  /** @type {React.MutableRefObject<MetroHelper>} */
  const metroHelperRef = useRef();

  window.hlsSourceHandler.register(videojs);

  /** @type {videojs.PlayerOptions} */
  const dataSetup = {
    ...BASE_VIDEOJS_OPTIONS,
    autoplay,
    sources,
    controlBar: {
      ...BASE_VIDEOJS_OPTIONS.controlBar,
      fullscreenToggle: enableFullscreenControl,
      audioTrackButton: enableAudioTrackButton,
      playbackRateMenuButton: !getDeviceMediaInfo().isTouch,
    },
    poster: thumbUrl,
  };

  const initMetroHelper = () => {
    const metroUser = new MetroUser(clientId, '', subscriptionId, subscriptionStatus);
    const sessionId = Math.random().toString(36).slice(2);
    metroHelperRef.current = new MetroHelper('webapp', environment, metroUser, sessionId);
  };

  /**
   * Toggles text tracks
   * @param {videojs.Player} player
   */
  const handleTextTrackToggle = (player) => {
    const tracks = player.textTracks();

    // eslint-disable-next-line no-underscore-dangle
    const englishTrack = tracks.tracks_.find(
      (track) => track.kind === 'subtitles' && track.language === 'en',
    );

    const trackOn = 'showing';
    const trackOff = 'hidden';
    englishTrack.mode = englishTrack.mode === trackOn ? trackOff : trackOn;
  };

  /**
   * A play event happened
   * @param {videojs.Player} player
   */
  const onVideoPlay = (player) => {
    dispatch({ type: 'play' });
    player.trigger(`add-${END_SCREEN_NAME}-class`, 'hide');
  };

  const onVideoTimeUpdate = () => {
    const player = videojs(videoRef.current);
    const metroHelper = metroHelperRef.current;

    if (ctaBannerProps) {
      const currentTime = Math.floor(player.currentTime());
      if (currentTime > 4) {
        player.trigger(`remove-${CTA_BANNER_NAME}-class`, 'hide');
      }

      const oneSecond = 1000;
      setTimeout(() => {
        player.trigger(`update-${CTA_BANNER_NAME}`, {
          ...ctaBannerProps,
          isRebrand,
          metroHelper,
          videoPlayMetadata,
          currentTime,
          setActiveListener: true,
          player,
          videoPlayerRefs: { videoRef, videoPlayerRef },
        });
      }, oneSecond);
    }
  };

  /**
   * Video ended event happened
   * @param {videojs.Player} player
   */
  const onVideoEnd = (player) => {
    dispatch({ type: 'ended' });
    player.trigger(`remove-${END_SCREEN_NAME}-class`, 'hide');
  };

  /**
   * This function triggers size changes for vjs components
   * @param {videojs.Player} player
   */
  const onFullScreenChange = (player) => {
    player.trigger(`update-${END_SCREEN_NAME}`, { ...endScreenProps, player });
  };

  /**
   * Play rate change happened
   * @param {videojs.Player} player
   */
  const onRateChange = (player) => {
    const metroHelper = metroHelperRef.current;
    const playbackRate = player.playbackRate();
    const metroPlaybackChangedEvent = new MetroEvent(
      'video_player.playback_speed_changed',
      'playback_speed',
      // Dev events table "metric value" is an INT4 e.g. 1.5 = 150, 1.25 = 125
      String(Math.round(playbackRate * 100)),
    );
    metroHelper.sendMetrics([metroPlaybackChangedEvent]);
  };

  const metricTextTrackFirstClick = () => {
    const metroHelper = metroHelperRef.current;
    const subtitleFileParts = subtitleFileSrc.split('/');

    const eventName = 'video_player.caption_button_clicked';
    const emptyUnit = null;
    const emptyValue = null;
    const videoId = subtitleFileParts[4];

    const metroEvent = new MetroEvent(eventName, emptyUnit, emptyValue, videoId);
    metroHelper.sendMetrics([metroEvent]);
  };

  /**
   * @param {videojs.Player} player
   */
  const registerVjsComponents = (player) => {
    const metroHelper = metroHelperRef.current;
    const emptyOptions = {};
    const controlBarIndexPosition = 3;

    registerVjsComponent(videojs, END_SCREEN_NAME, VideoEndScreen, {
      ...endScreenProps,
      player,
      hasCta: Boolean(ctaBannerProps),
      isTouch: Boolean(getDeviceMediaInfo().isTouch),
      isRebrand,
    });
    player.addChild(END_SCREEN_NAME, emptyOptions, controlBarIndexPosition);

    if (getDeviceMediaInfo().isTouch) {
      registerVjsComponent(videojs, PLAYBACK_RATE_BUTTONS, VideoPlaybackRateButtons, {
        player,
        playbackRates: BASE_VIDEOJS_OPTIONS.playbackRates,
        isRebrand,
      });
      player.addChild(PLAYBACK_RATE_BUTTONS, emptyOptions, controlBarIndexPosition);
    }

    if (ctaBannerProps) {
      const { link, title } = ctaBannerProps;
      registerVjsComponent(videojs, CTA_BANNER_NAME, VideoCtaBanner, {
        link,
        title,
        metroHelper,
        videoPlayMetadata,
        player,
        videoPlayerRefs: { videoRef, videoPlayerRef },
        isRebrand,
      });
      player.addChild(CTA_BANNER_NAME, emptyOptions, controlBarIndexPosition);
    }
  };

  /**
   * Add support for hover devices to be able to tap the CC button once to show menu
   * @param {videojs.Player} player
   */
  const handleTouchDeviceCCButton = (player) => {
    const subsCaps = player.getDescendant('ControlBar', 'SubsCapsButton');
    const el = subsCaps?.contentEl();
    const button = el?.querySelector('button.vjs-subs-caps-button');

    if (subsCaps && el && button) {
      const observer = new MutationObserver((records) => {
        const element = /** @type HTMLElement */ (records[0]?.target);
        if (element !== button) {
          return;
        }

        if (element.getAttribute('aria-expanded') === 'true') {
          subsCaps.addClass('vjs-hover');
        } else {
          subsCaps.removeClass('vjs-hover');
        }
      });

      observer.observe(button, { attributeFilter: ['aria-expanded'] });

      player.one('dispose', () => {
        observer.disconnect();
      });
    }
  };

  /**
   * Handles various captions listeners
   * @param {videojs.Player} player
   */
  const handleCaptionsListeners = (player) => {
    const playerNode = getRootNode(videoPlayerRef);
    const closedCaptionsButton = playerNode.getElementsByClassName('vjs-subs-caps-button')[0];
    if (!closedCaptionsButton) {
      return;
    }
    const textTrackContainer = playerNode.querySelector('.vjs-text-track-display');

    // handle cc button click
    closedCaptionsButton.addEventListener('click', () => handleTextTrackToggle(player));
    player.one('texttrackchange', () => metricTextTrackFirstClick());

    const tracks = player.textTracks();
    if (tracks && tracks[0]) {
      // handle aria label for cc button
      closedCaptionsButton.setAttribute('aria-pressed', 'false');
      closedCaptionsButton.setAttribute('aria-label', 'Closed Captions');
      tracks.on('change', () => {
        closedCaptionsButton.setAttribute(
          'aria-pressed',
          tracks[0].mode === 'showing' ? 'true' : 'false',
        );
      });

      // handle text track cue change
      tracks[0].on('cuechange', () => {
        handleCueChange(textTrackContainer, tracks[0].activeCues[0]);
      });
    }

    if (getDeviceMediaInfo().isTouch) {
      handleTouchDeviceCCButton(player);
    }
  };

  /**
   * @param {videojs.Player} player
   */
  const onPlayerReady = (player) => {
    registerVjsComponents(player);

    player.on('play', () => onVideoPlay(player));
    player.on('pause', () => {
      dispatch({ type: 'pause' });
    });
    player.on('timeupdate', onVideoTimeUpdate);
    player.on('ended', () => onVideoEnd(player));
    player.on('fullscreenchange', () => onFullScreenChange(player));
    player.on('ratechange', () => onRateChange(player));
    player.on('seeking', () => dispatch({ type: 'seeking' }));
    player.on('seeked', () => dispatch({ type: 'seeked' }));
    player.on('touchstart', (event) => {
      if (event.target && event.target.nodeName === 'VIDEO') {
        if (player.paused()) {
          player.play();
        } else {
          player.pause();
        }
      }
    });

    if (subtitleFileSrc) {
      handleCaptionsListeners(player);
    }

    eventListeners.forEach((event) => {
      player.on(event.name, event.function);
    });

    playerListener(player);
  };

  useEffect(() => {
    initMetroHelper();

    const player = videojs(videoRef.current, dataSetup);
    player.on('ready', () => {
      onPlayerReady(player);

      const volumeControlListener = (type, playerNode) => {
        const volumePanel = playerNode.getElementsByClassName('vjs-volume-panel')[0];
        const timeControl = playerNode.querySelectorAll(
          '.vjs-time-control:not(.vjs-remaining-time)',
        );
        const timeout = type === 'mouseover' ? 0 : 500;
        setTimeout(() => {
          Array.from(timeControl).forEach((control) => {
            if (type === 'mouseover') {
              volumePanel.classList.add('open-volume-panel');
              control.classList.add('hide-time');
            } else {
              volumePanel.classList.remove('open-volume-panel');
              control.classList.remove('hide-time');
            }
          });
        }, timeout);
      };

      const handleVolumeControl = () => {
        if (videoPlayerRef && videoPlayerRef.current.offsetWidth >= SMALL_VIDEO_SIZE) {
          return;
        }
        const playerNode = getRootNode(videoPlayerRef);
        const volumePanel = playerNode.getElementsByClassName('vjs-volume-panel')[0];
        if (volumePanel) {
          volumePanel.addEventListener('mouseover', () =>
            volumeControlListener('mouseover', playerNode),
          );
          volumePanel.addEventListener('mouseleave', () =>
            volumeControlListener('mouseleave', playerNode),
          );
        }
      };

      handleVolumeControl();

      player.on('dispose', () => {
        const playerNode = getRootNode(videoPlayerRef);
        const volumePanel = playerNode.getElementsByClassName('vjs-volume-panel')[0];
        if (volumePanel) {
          volumePanel.removeEventListener('mouseover', volumeControlListener);
          volumePanel.removeEventListener('mouseout', volumeControlListener);
        }
      });
    });

    if (videoPlayerRef) {
      setVideoWidth(videoPlayerRef.current.offsetWidth);
    }

    addPlayerMessageEventHandler(videoRef);

    return function cleanup() {
      removePlayerMessageEventHandler();
      player.dispose();
    };
  }, []);

  /** @type {Record<keyof HTMLVideoElement, unknown>} */
  const videoAttributes = {};
  if (shouldPlayInline) {
    videoAttributes.playsInline = true;
  }

  return (
    <div
      className={classNames(className, 'custom', {
        'vjs-custom-mobile': getDeviceMediaInfo().isTouch,
        'controls-disabled': thumbUrl !== '' && !playButtonState.hasPlayed,
        'small-video': videoWidth < SMALL_VIDEO_SIZE,
      })}
      ref={videoPlayerRef}
    >
      <div className="play-button-container">
        {playButtonState.showPlayButton && (
          <ClickableElement
            className={`icon--play ${!playButtonState.hasPlayed ? 'initial' : 'subsequent'}`}
            onClick={() => {
              const player = videojs(videoRef.current);
              player.play();
            }}
          >
            <LargePlay
              transparentBackground={playButtonState.hasPlayed}
              backgroundFill={!playButtonState.hasPlayed ? colors.white : colors.almostBlack}
              foregroundFill={!playButtonState.hasPlayed ? colors.taosSky : colors.white}
            />
          </ClickableElement>
        )}
      </div>
      <video
        id="video"
        ref={videoRef}
        className={`video-js vjs-custom-skin ${thumbUrl ? 'vjs-has-thumb' : ''}`}
        crossOrigin="anonymous"
        {...videoAttributes}
      >
        {sources.map((source) => (
          <source src={source.src} type={source.type} key={source.src} />
        ))}
        {subtitleFileSrc && (
          <track
            label="English (auto-generated)"
            kind="subtitles"
            srcLang="en"
            src={subtitleFileSrc}
          />
        )}
        Sorry, your browser doesn&apos;t support embedded videos.
      </video>
    </div>
  );
}

// eslint-disable-next-line no-func-assign
VideoPlayer = forwardRef(VideoPlayer);

VideoPlayer.propTypes = {
  /** Emotion base style */
  className: PropTypes.string,
  /** Should the player autoplay */
  autoplay: PropTypes.bool,
  /** Thumbnail image url */
  thumbUrl: PropTypes.string,
  /** Shows the fullscreen button in video controls. */
  enableFullscreenControl: PropTypes.bool,
  /** Should the video play inline? */
  shouldPlayInline: PropTypes.bool,
  /** Shows the audio track button (if applicable) in video controls. */
  enableAudioTrackButton: PropTypes.bool,
  /**
   * Array of video sources provided to VideoJs - Requires "type" and "src",
   * otherwise just use the "src"
   */
  sources: PropTypes.arrayOf(
    PropTypes.shape({
      type: PropTypes.string.isRequired,
      src: PropTypes.string.isRequired,
    }).isRequired,
  ).isRequired,
  /**
   * Url of S3 VTT file, currently this is per videoId not video source.
   * This means there is 1 subtitle file only.
   * English is the assumed track language.
   * */
  subtitleFileSrc: PropTypes.string,
  /** Object of CTA Banner Props - Requires "title", "link", "userClientId", "userSubscriptionId",
   * "userSubscriptionStatus", "environment"
   * */
  ctaBannerProps: PropTypes.shape({
    className: PropTypes.string,
    title: PropTypes.string.isRequired,
    link: PropTypes.string.isRequired,
  }),
  clientId: PropTypes.string.isRequired,
  subscriptionId: PropTypes.string.isRequired,
  subscriptionStatus: PropTypes.string.isRequired,
  environment: PropTypes.oneOf(['LOCAL', 'DEV', 'PROD']).isRequired,
  /** Object of End Screen Props - Requires "buttons" */
  endScreenProps: PropTypes.shape({
    className: PropTypes.string,
    /** A list of button objects to display on the end screen */
    buttons: PropTypes.arrayOf(
      PropTypes.shape({
        text: PropTypes.string.isRequired,
        callback: PropTypes.func.isRequired,
        element: PropTypes.elementType.isRequired,
        name: PropTypes.string,
      }).isRequired,
    ),
    endScreen: PropTypes.node,
  }).isRequired,

  /** An array of event - function pairs that gets directly applied to vjs */
  eventListeners: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      function: PropTypes.func.isRequired,
    }),
  ),
  /** An callback to pass into the videoPlayer component to access the player object when ready */
  playerListener: PropTypes.func,
  /** Video Played MetaData */
  videoPlayMetadata: PropTypes.shape({
    videoViewId: PropTypes.string,
    videoId: PropTypes.string,
    jobId: PropTypes.string,
    browserViewerId: PropTypes.string,
  }),
  /** Whether this video should be in mobile or desktop play mode */
  playPauseMode: PropTypes.bool,
  isRebrand: PropTypes.bool,
};

VideoPlayer.defaultProps = {
  autoplay: false,
  className: '',
  ctaBannerProps: null,
  enableAudioTrackButton: false,
  enableFullscreenControl: true,
  eventListeners: [],
  playerListener: (player) => {},
  playPauseMode: false,
  shouldPlayInline: false,
  subtitleFileSrc: null,
  thumbUrl: null,
  videoPlayMetadata: null,
  isRebrand: false,
};

// play button size in %
const playButtonSize = 35;

const StyledVideoPlayer = styled(VideoPlayer)`
  position: relative;
  overflow: auto;
  ${videojsDefaultSkin()}

  > .play-button-container {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    display: flex;
    align-items: center;
    justify-content: center;

    > .icon--play {
      position: relative;
      height: ${playButtonSize}%;
      z-index: 2;
      cursor: pointer;

      &.subsequent {
        ${'' /* magic number to move play button up slightly to center between controls & top */}
        top: -5%;
      }

      svg {
        width: 100%;
        height: 100%;

        .outer {
          animation: pulsate1 1.5s linear 0s infinite;
          stroke: ${colors.white};
          stroke-width: 3px;
          transform-origin: center;
        }
      }

      ${'' /* after the initial view, the icon should not animate */}
      &.subsequent {
        margin-bottom: 25px;

        svg .outer {
          animation: none;
          stroke: none;
        }
      }
    }
  }

  &.video-preview.vjs-custom-mobile {
    .play-button-container {
      .icon--play.subsequent {
        margin-bottom: 17px;
      }
    }
  }

  .video-js {
    overflow: hidden;
    min-width: 220px;
    height: 100%;
    width: 100%;
    color: ${colors.fog};

    .vjs-control {
      outline: none;

      &.vjs-playback-rate {
        margin-left: 6px;
        width: 33px;
      }

      /* for keyboard focus, do not have the text shadow (this will not work in Safari) */
      &:focus:focus-visible {
        text-shadow: none;
      }
    }

    /* control focus states for all player controls */
    .vjs-play-control,
    .vjs-mute-control,
    .vjs-volume-control,
    .vjs-progress-control,
    .vjs-playback-rate.vjs-control,
    .vjs-subs-caps-button,
    .vjs-fullscreen-control {
      /* default focus state for browsers that do not support :focus-visible */
      &:focus,
      &:focus-within {
        outline: 2px solid #1298ca;
      }

      &:focus-visible {
        outline: 2px solid #1298ca;
      }

      /* remove outline on click focus for browsers that support it */
      &:focus:not(:focus-visible) {
        outline: none;
      }
    }

    .vjs-volume-control .vjs-volume-bar:focus,
    .vjs-progress-control:focus-within .vjs-progress-holder {
      outline: none;
    }

    /*
      slider bars get an inner focus outline that we don't want
      and playback rate is just a weird button
     */
    .vjs-volume-control .vjs-volume-bar:focus-visible,
    .vjs-playback-rate .vjs-playback-rate:focus-visible,
    .vjs-progress-control .vjs-progress-holder:focus-visible {
      outline: none;
    }

    /*
      removes the height transition from this element which looks
      weird with focus states
    */
    .vjs-volume-panel .vjs-volume-control.vjs-volume-horizontal {
      transition: visibility 1s, opacity 1s, width 1s, left 1s 1s, top 1s 1s;
    }
  }

  &.custom {
    position: relative;
  }

  .vjs-custom-skin {
    .vjs-progress-control.vjs-control {
      bottom: 25px;
      width: 100%;
      position: inherit;
      margin: auto;

      .vjs-slider {
        margin: 0px 21px;
        height: 5px;
        border-radius: 2px;
        background-color: #e5e5e5;

        .vjs-load-progress {
          background-color: #9b9b9b;
          height: 5px;
          border-radius: 2px;

          div {
            background-color: #9b9b9b;
          }
        }

        .vjs-mouse-display {
          font-size: 10px;
        }

        .vjs-time-tooltip {
          font-size: 10px;
        }
      }

      .vjs-play-progress {
        border-radius: 2px;
      }

      .vjs-play-progress:before {
        font-size: 1.5em;
        z-index: 0;
      }

      &:hover .vjs-progress-holder {
        font-size: inherit;
      }
    }

    button.vjs-fullscreen-control.vjs-control.vjs-button {
      margin-right: 35px;
      margin-left: -10px;
    }

    button.vjs-play-control.vjs-control.vjs-button {
      margin-left: 30px;
      margin-right: -10px;
    }

    .vjs-volume-panel.vjs-control {
      margin-left: -3px;
    }

    .vjs-time-control.vjs-current-time {
      margin-left: -7px;
    }

    .vjs-time-control.vjs-current-time,
    .vjs-time-control.vjs-time-control,
    .vjs-time-control.vjs-duration {
      top: 0.25px;
    }

    .vjs-volume-panel.vjs-control.vjs-volume-panel-horizontal .vjs-volume-horizontal {
      top: 2.75px;
      margin-left: -7px;
    }

    .vjs-slider-horizontal .vjs-volume-level:before {
      top: -3px;
    }

    .vjs-subs-caps-button.vjs-menu-button.vjs-menu-button-popup.vjs-control.vjs-button {
      margin-left: -6px;
    }

    .vjs-playback-rate.vjs-control {
      margin-right: 5px;
      > div.vjs-playback-rate-value {
        margin-top: 13px;
        color: ${colors.almostBlack};
        background: #eee; /* match other colors */
        height: 15px;
        align-items: center;
        font-size: 10px;
        width: 24px;
        border-radius: 2px;
        justify-content: center;
        font-weight: 500;
      }

      .vjs-menu {
        transform: translate(-8px, 13px);
        li {
          font-size: 1em;
        }
      }
    }

    .vjs-subs-caps-button .vjs-menu {
      bottom: -16px;
      li {
        font-size: 1em;
      }
    }

    .vjs-control-bar {
      background: none;
      bottom: 8px;
      font-size: 12px;
      z-index: 1;

      span.vjs-icon-placeholder:before {
        line-height: 1.9;
      }
    }

    /* Captions Control (when control bar is up) */
    .vjs-text-track-display .vjs-text-track-cue {
      transition: inset 0.8s cubic-bezier(0.2, 0.8, 0.2, 1);
      inset: 81% 0px 0px !important; // vjs uses inline styles, need this to override
      z-index: 1;
      font: 14px sans-serif !important;

      div {
        line-height: 170%;
      }
    }

    .vjs-text-track-display.large.two-lines .vjs-text-track-cue,
    .vjs-text-track-display.medium.two-lines .vjs-text-track-cue {
      inset: 77% 0px 0px !important; // vjs uses inline styles, need this to override
    }

    .vjs-text-track-display.two-lines .vjs-text-track-cue {
      inset: 73% 0px 0px !important; // vjs uses inline styles, need this to override
    }

    .vjs-text-track-display.large .vjs-text-track-cue {
      inset: 87% 0px 0px !important; // vjs uses inline styles, need this to override
      font: 25px sans-serif !important;
    }

    .vjs-text-track-display.medium .vjs-text-track-cue {
      inset: 84% 0px 0px !important; // vjs uses inline styles, need this to override
      font: 22px sans-serif !important;
    }

    &.vjs-fullscreen .vjs-text-track-display .vjs-text-track-cue {
      inset: 90% 0px 0px !important; // vjs uses inline styles, need this to override
      font: 25px sans-serif !important;
    }

    &.vjs-fullscreen .vjs-text-track-display.two-lines .vjs-text-track-cue {
      inset: 86% 0px 0px !important; // vjs uses inline styles, need this to override
    }

    .vjs-control {
      text-align: left;
    }

    .vjs-remaining-time.vjs-time-control.vjs-control {
      display: none;
    }

    .vjs-time-control {
      display: block;
      padding-top: 3px;
    }

    .vjs-duration.vjs-time-control.vjs-control {
      flex-grow: 1;
    }

    .vjs-time-control.vjs-time-divider {
      padding: 0px;
      min-width: 0;
      padding-top: 4px;
    }

    .vjs-time-control.vjs-current-time {
      padding-right: 5px;
    }

    .vjs-time-control.vjs-duration {
      padding-left: 5px;
    }
  }

  /* Small Video Overrides */
  &.custom.small-video .vjs-custom-skin {
    button.vjs-play-control.vjs-control.vjs-button {
      margin-left: 0;
    }

    .vjs-volume-panel.open-volume-panel {
      flex-grow: 1;
    }

    .vjs-time-control:not(.vjs-remaining-time).hide-time {
      display: none;
    }

    button.vjs-fullscreen-control.vjs-control.vjs-button {
      margin-right: 0;
    }
  }

  /* Mobile Specific Overrides */
  &.custom.vjs-custom-mobile .vjs-custom-skin {
    .vjs-control-bar {
      bottom: 8px;
    }

    .vjs-text-track-display .vjs-text-track-cue {
      inset: 71% 0px 0px !important; // vjs uses inline styles, need this to override
    }

    .vjs-text-track-display.two-lines .vjs-text-track-cue {
      inset: 60% 0px 0px !important; // vjs uses inline styles, need this to override.
    }

    &.vjs-touch-enabled.vjs-playing.vjs-user-inactive .vjs-control-bar {
      bottom: -5em;
    }

    .vjs-progress-control,
    &.vjs-paused .vjs-progress-control {
      bottom: 30px;
      height: 25px;
    }

    .vjs-volume-panel,
    .vjs-subs-caps-button,
    .vjs-fullscreen-control {
      width: 4em;
      height: 44px;
    }

    .vjs-play-control {
      display: none;
    }

    .vjs-volume-panel {
      margin-left: 5px;

      .vjs-volume-horizontal {
        display: none;
      }
    }

    .vjs-current-time {
      padding-right: 1px;
      margin-left: -5px;
    }

    .vjs-duration {
      padding-left: 1px;
    }

    .vjs-progress-control {
      .vjs-slider {
        margin: 0px;
        border-radius: 0px;

        .vjs-load-progress,
        .vjs-play-progress {
          border-radius: 0px;
        }
      }
    }

    .vjs-fullscreen-control.vjs-control.vjs-button {
      margin: 0px;
    }
  }

  &.video-preview.custom.vjs-custom-mobile {
    .vjs-control-bar {
      bottom: 4px;
    }

    .vjs-progress-control,
    .vjs-paused .vjs-progress-control {
      bottom: 27px;
      height: 25px;
    }
  }

  .vjs-custom-skin.vjs-paused .vjs-progress-control.vjs-control {
    bottom: 25px;
  }

  .vjs-progress-holder.vjs-slider {
    background-color: ${colors.boulder};
  }

  .video-js .vjs-load-progress div {
    background-color: ${colors.silverSandAlt};
  }

  /* Custom Overlay */
  .vjs-custom-skin:not(.vjs-ended)::before {
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    display: block;
    background: transparent
      linear-gradient(
        180deg,
        rgba(255, 255, 255, 0.1) 0%,
        rgba(167, 166, 166, 0.2) 51%,
        rgba(41, 41, 41, 1) 100%
      );
    opacity: 0.7;
    content: ' ';
    z-index: 1;
    pointer-events: none;
    transition: all 0.8s cubic-bezier(0.2, 0.8, 0.2, 1);
  }

  &.controls-disabled {
    .vjs-control-bar {
      display: none;
    }
    .vjs-custom-skin::before {
      content: none;
    }
  }

  &.video-preview .vjs-paused:not(.vjs-has-started).vjs-custom-skin::before {
    opacity: 0;
  }

  &.vjs-custom-mobile .vjs-custom-skin::before {
    background: ${colors.almostBlack};
  }

  .vjs-playing.vjs-user-inactive .vjs-playback-rate-buttons .playback-buttons,
  .vjs-paused:not(.vjs-has-started) .vjs-playback-rate-buttons .playback-buttons {
    top: -25px;
  }

  .vjs-playing.vjs-user-inactive::before {
    opacity: 0;
  }

  /* Start VJS ControlBar CSS */
  .vjs-control-bar {
    display: flex;
    background-color: ${colors.almostBlack};
    transition: all 0.8s cubic-bezier(0.2, 0.8, 0.2, 1);
    z-index: 1;
  }

  .vjs-playing.vjs-user-inactive .vjs-control-bar {
    bottom: -5em;
  }

  /* Captions Control (when control bar is not visible) */
  .vjs-playing.vjs-user-inactive .vjs-text-track-display .vjs-text-track-cue {
    inset: 95% 0px 0px !important; // vjs uses inline styles, need this to override.
  }

  .vjs-playing.vjs-user-inactive .vjs-text-track-display.two-lines .vjs-text-track-cue {
    inset: 85% 0px 0px !important; // vjs uses inline styles, need this to override.
  }

  &.custom.vjs-custom-mobile
    .vjs-playing.vjs-user-inactive
    .vjs-text-track-display
    .vjs-text-track-cue {
    inset: 95% 0px 0px !important; // vjs uses inline styles, need this to override.
  }

  &.vjs-custom-mobile .vjs-paused:not(.vjs-has-started) .vjs-control-bar,
  &.video-preview .vjs-paused:not(.vjs-has-started) .vjs-control-bar {
    visibility: hidden;
    display: unset;
  }

  /* End VJS ControlBar CSS */

  /* Start VJS CTA CSS */
  .vjs-cta-banner {
    position: absolute;
    top: 23px;
    right: 17px;
    z-index: 1;
    transform-origin: top right;
    transition: opacity 0.5s;
  }
  .vjs-cta-banner.hide {
    opacity: 0;
    pointer-events: none;
  }
  /* End VJS CTA CSS */

  /* Start VJS end-screen CSS */
  .vjs-end-screen {
    ${absoluteTopLeft()}
    ${transitionTransformMedium()}
    width: 100%;
    height: 100%;
    z-index: 0;
  }
  .vjs-end-screen.hide {
    transform: translateY(-100%);
  }
  /* END VJS end-screen CSS */

  @keyframes pulsate1 {
    0% {
      transform: scale(1);
      opacity: 1;
    }
    80% {
      opacity: 0.6;
    }
    100% {
      transform: scale(1.18);
      opacity: 0;
    }
  }

  @media (prefers-reduced-motion) {
    .play-button-container > .icon--play svg .outer {
      animation: none;
    }
  }
`;

export default StyledVideoPlayer;
